import {
    HttpClient,
    HttpErrorResponse,
    HttpHeaders,
    HttpParams,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { Http, Request, Response, createURLParser, validateUrl } from '@mdd/api';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpUrlEncodingCodec } from './httpUrlEncodingCodec';

export type ResponseType = 'arraybuffer' | 'blob' | 'json' | 'text';
@Injectable()
export class MddHttpService implements Http {
    // TODO: Server error types
    error = new EventEmitter<Response<any>>();

    constructor(private httpClient: HttpClient) {}

    request<T, V>(request: Request<T>): Promise<Response<V>>;

    request<T, V>(request: Request<T>, responseType: ResponseType): Promise<Response<V>>;

    request<V>(request: Request<any>, responseType: ResponseType): Promise<Response<V>>;

    request<V>(request: Request<any>): Promise<Response<V>>;

    request<T, V>(request: Request<T>, responseType: ResponseType = 'json'): Promise<Response<V>> {
        return new Promise((resolve) => {
            requestAnimationFrame(resolve);
        })
            .then(() => {
                // Validate URL before initialization of the request
                const parser = createURLParser(request.url, environment.apiBase);
                parser.search = request.search || parser.search;
                const finalUrl = parser.href;

                if (
                    !validateUrl(finalUrl, {
                        allowedBaseUrl: environment.apiBase,
                        allowRelativeUrls: true
                    })
                ) {
                    throw this.getBlockedUnsafeUrlError();
                }
            })
            .then(() => {
                return this.httpClient
                    .request(this.toHttpRequest(request, responseType))
                    .pipe(
                        filter((event) => {
                            return event instanceof HttpResponse;
                        })
                    )
                    .toPromise();
            })
            .then((httpResponse: HttpResponse<V>) => {
                return this.toResponse(httpResponse);
            })
            .catch((httpError: HttpErrorResponse) => {
                const response = this.toResponse(httpError);
                this.error.next(response);
                throw response;
            });
    }

    requestPdfExportAsset(url: string): Promise<Response<string>> {
        return new Promise((resolve) => {
            requestAnimationFrame(resolve);
        })
            .then(() => {
                // Rebuild the URL from the trusted origin and validated path segments
                // instead of forwarding the input string, so the request target cannot
                // be redirected outside same-origin /assets/ (SSRF)
                const safeUrl = this.buildPdfExportAssetUrl(url);

                if (!safeUrl) {
                    throw this.getBlockedUnsafeUrlError();
                }

                // Additive belt-and-suspenders: re-validate the already-rebuilt,
                // absolute same-origin URL through the shared validateUrl guard used
                // by request() above. buildPdfExportAssetUrl remains the primary
                // allowlist defense; this reuses the recognized sanitizer so the SSRF
                // guarantee is explicit at the sink. safeUrl is absolute, so relative
                // URLs are disallowed here, and no allowedBaseUrl is passed so the
                // hostname falls back to the current origin the URL was built from.
                if (!validateUrl(safeUrl, { allowRelativeUrls: false })) {
                    throw this.getBlockedUnsafeUrlError();
                }

                return this.httpClient
                    .request(this.toHttpRequest({ url: safeUrl }, 'text'))
                    .pipe(
                        filter((event) => {
                            return event instanceof HttpResponse;
                        })
                    )
                    .toPromise();
            })
            .then((httpResponse: HttpResponse<string>) => {
                return this.toResponse(httpResponse);
            })
            .catch((httpError: HttpErrorResponse) => {
                const response = this.toResponse(httpError);
                this.error.next(response);
                throw response;
            });
    }

    private toHttpRequest<T>(
        request: Request<T>,
        responseType: ResponseType = 'json'
    ): HttpRequest<T> {
        // TODO: Find a better way to specify Http implementation-specific options
        if (
            request.url.includes('payment-gateway') ||
            request.url.includes('register') ||
            request.url.includes('change_password')
        ) {
            responseType = 'text';
        }

        const parser = createURLParser(request.url, environment.apiBase);
        return new HttpRequest<T>(request.method || 'GET', parser.href, request.body!, {
            responseType,
            params: new HttpParams({
                fromString: request.search,
                encoder: new HttpUrlEncodingCodec()
            }),
            headers: new HttpHeaders(request.headers),
            withCredentials: request.withCredentials
        });
    }

    private buildPdfExportAssetUrl(url: string): string | null {
        if (typeof window === 'undefined') {
            return null;
        }

        try {
            const parser = createURLParser(url);
            const currentOrigin = createURLParser(window.location.href);

            if (
                parser.protocol !== currentOrigin.protocol ||
                parser.host !== currentOrigin.host ||
                !parser.pathname.startsWith('/assets/')
            ) {
                return null;
            }

            // decodeURIComponent throws on malformed sequences and normalizes
            // double-encoded traversal (%2e%2e) so the segment check below catches it
            const segments = parser.pathname
                .slice('/assets/'.length)
                .split('/')
                .map((segment) => decodeURIComponent(segment));

            if (segments.some((segment) => !segment || segment === '.' || segment === '..')) {
                return null;
            }

            const safePath = segments.map((segment) => encodeURIComponent(segment)).join('/');
            return `${currentOrigin.protocol}//${currentOrigin.host}/assets/${safePath}`;
        } catch (error) {
            return null;
        }
    }

    private getBlockedUnsafeUrlError(): HttpErrorResponse {
        return new HttpErrorResponse({
            error: '[SSRF Protection]: Blocked unsafe URL',
            status: 403
        });
    }

    private toResponse<V>(httpResponse: HttpResponse<V> | HttpErrorResponse): Response<V> {
        if (httpResponse instanceof HttpErrorResponse) {
            return {
                body: httpResponse.error,
                status: httpResponse.status
            };
        } else {
            return httpResponse as any;
        }
    }
}
